/**
 * Field name for displayId in camelCase (model layer)
 * This field should be generated by the database, not set explicitly
 */
const DISPLAY_ID_FIELD_NAME = "displayId";

export function enforceShape<T extends object>(obj: any, shape: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(shape)) {
    if (!Array.isArray(obj)) return [] as unknown as T;
    if (shape.length === 0) return obj as unknown as T;

    return obj.map((item) => enforceShape(item, shape[0])) as unknown as T;
  }

  const result: any = {};

  for (const key of Object.keys(shape) as (keyof T)[]) {
    const value = obj[key as string];
    const expected = shape[key];

    // Skip displayId when it's null - it should be generated by the database, not set explicitly
    // By skipping it, it becomes undefined, which won't be included in database inserts
    if (key === DISPLAY_ID_FIELD_NAME && value === null) {
      continue;
    }

    if (value !== undefined) {
      if (typeof expected === "object" && expected !== null && !Array.isArray(expected)) {
        // An empty object shape means "any object is valid" — pass through the value as-is.
        // Recursing into {} would strip all keys from the actual value.
        if (Object.keys(expected).length === 0) {
          result[key] = value;
        } else {
          result[key] = enforceShape(value, expected);
        }
      } else if (Array.isArray(expected)) {
        if (Array.isArray(value)) {
          // An empty array shape means the item structure is intentionally unspecified, such as
          // JSON arrays whose runtime shape is enforced by Zod at the API/model boundary.
          result[key] = expected.length === 0 ? value : value.map((v) => enforceShape(v, expected[0]));
        } else {
          result[key] = [];
        }
      } else {
        result[key] = value;
      }
    }
  }

  return result;
}
